import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  fetchInvoice,
  sendInvoice,
  voidInvoice,
  duplicateInvoice,
  deleteInvoice,
  fetchInvoices,
  checkPaymentStatus,
} from '../../store/actions/FinanceActions';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import {
  PiXLight,
  PiPaperPlaneTiltLight,
  PiCopyLight,
  PiProhibitLight,
  PiCopySimpleLight,
  PiEnvelopeLight,
  PiClockLight,
  PiCheckCircleLight,
  PiWarningCircleLight,
  PiXCircleLight,
  PiEyeLight,
  PiTrashLight,
  PiWarningLight,
  PiPencilSimpleLight,
  PiArrowsClockwiseLight,
} from 'react-icons/pi';
import { FinanceInvoice } from '../../store/reducer/FinanceReducer';
import Spinner from '../shared/TailwindComponents/Spinner';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: number | null;
  organizationId: number;
  onRefresh?: () => void;
  onEdit?: (invoice: FinanceInvoice) => void;
}

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const statusConfig: Record<string, { color: any; icon: React.ReactNode; label: string }> = {
  draft: { color: 'gray', icon: <PiClockLight className="w-4 h-4" />, label: 'Draft' },
  sent: { color: 'blue', icon: <PiEnvelopeLight className="w-4 h-4" />, label: 'Sent' },
  viewed: { color: 'purple', icon: <PiEyeLight className="w-4 h-4" />, label: 'Viewed' },
  paid: { color: 'green', icon: <PiCheckCircleLight className="w-4 h-4" />, label: 'Paid' },
  overdue: { color: 'red', icon: <PiWarningCircleLight className="w-4 h-4" />, label: 'Overdue' },
  void: { color: 'yellow', icon: <PiXCircleLight className="w-4 h-4" />, label: 'Void' },
  partially_paid: { color: 'purple', icon: <PiCheckCircleLight className="w-4 h-4" />, label: 'Partially Paid' },
};

const InvoiceDetailModal: React.FC<Props> = ({ isOpen, onClose, invoiceId, organizationId, onRefresh, onEdit }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const invoice = useAppSelector((state: RootState) => state.finance.selectedInvoice);
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && invoiceId && organizationId) {
      dispatch(fetchInvoice(invoiceId, organizationId));
    }
  }, [isOpen, invoiceId, organizationId, dispatch]);

  // Must be before early return to keep hook count stable
  const timelineEvents = useMemo(() => {
    const inv = invoice;
    if (!inv) return [];
    const events = [
      { label: t('Created'), date: inv.created_at, icon: <PiClockLight className="w-3.5 h-3.5" /> },
    ];
    if (inv.sent_at) events.push({ label: t('Sent'), date: inv.sent_at, icon: <PiEnvelopeLight className="w-3.5 h-3.5" /> });
    if (inv.viewed_at) events.push({ label: t('Viewed'), date: inv.viewed_at, icon: <PiEyeLight className="w-3.5 h-3.5" /> });
    if (inv.paid_at) events.push({ label: t('Paid'), date: inv.paid_at, icon: <PiCheckCircleLight className="w-3.5 h-3.5" /> });
    if (inv.voided_at) events.push({ label: t('Voided'), date: inv.voided_at, icon: <PiXCircleLight className="w-3.5 h-3.5" /> });
    if (inv.reminder_sent_at) events.push({ label: t('Reminder Sent'), date: inv.reminder_sent_at, icon: <PiEnvelopeLight className="w-3.5 h-3.5" /> });
    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [invoice, t]);

  const handleSend = async () => {
    if (!invoice) return;
    const result = await dispatch(sendInvoice(invoice.id, organizationId, invoice.client_email || undefined));
    if (result) {
      dispatch(fetchInvoice(invoice.id, organizationId));
      onRefresh?.();
    }
  };

  const handleVoid = async () => {
    if (!invoice) return;
    await dispatch(voidInvoice(invoice.id, organizationId));
    dispatch(fetchInvoice(invoice.id, organizationId));
    onRefresh?.();
  };

  const handleDuplicate = async () => {
    if (!invoice) return;
    const result = await dispatch(duplicateInvoice(invoice.id, organizationId));
    if (result) {
      onRefresh?.();
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    const result = await dispatch(deleteInvoice(invoice.id, organizationId));
    if (result) {
      setShowDeleteConfirm(false);
      onRefresh?.();
      onClose();
    }
  };

  const [checkingPayment, setCheckingPayment] = useState(false);

  const handleCheckPayment = async () => {
    if (!invoice) return;
    setCheckingPayment(true);
    try {
      const updated = await dispatch(checkPaymentStatus(invoice.id, organizationId));
      if (updated) {
        dispatch(fetchInvoice(invoice.id, organizationId));
        onRefresh?.();
      }
    } finally {
      setCheckingPayment(false);
    }
  };

  const copyPaymentLink = () => {
    if (invoice?.stripe_payment_link) {
      navigator.clipboard.writeText(invoice.stripe_payment_link);
    }
  };

  if (!isOpen) return null;

  const inv = invoice;
  const status = statusConfig[inv?.status || 'draft'] || statusConfig.draft;

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] glass rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {inv?.invoice_number || t('Invoice')}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {inv?.client_name || t('No client')}
                {inv?.matter_title && ` - ${inv.matter_title}`}
              </p>
            </div>
            <Badge color={status.color}>
              <span className="flex items-center gap-1">
                {status.icon}
                {t(status.label)}
              </span>
            </Badge>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading?.selectedInvoice ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="glyph" className="!h-8 !w-8" />
            </div>
          ) : inv ? (
            <div className="space-y-6">
              {/* Info grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <InfoCard label={t('Total')} value={formatCurrency(inv.total_amount, inv.currency)} highlight />
                <InfoCard label={t('Amount Due')} value={formatCurrency(inv.amount_due, inv.currency)} highlight={inv.amount_due > 0} />
                <InfoCard label={t('Amount Paid')} value={formatCurrency(inv.amount_paid, inv.currency)} />
                <InfoCard
                  label={t('Due Date')}
                  value={inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'}
                />
              </div>

              {/* Bill To section */}
              {(inv.client_name || inv.client_email || inv.billing_address) && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">{t('Bill To')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{inv.client_name || '—'}</p>
                  {inv.client_email && <p className="text-sm text-gray-600 dark:text-gray-400">{inv.client_email}</p>}
                  {inv.billing_address && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {inv.billing_address.line1 && <p>{inv.billing_address.line1}</p>}
                      {inv.billing_address.line2 && <p>{inv.billing_address.line2}</p>}
                      <p>
                        {[inv.billing_address.city, inv.billing_address.state, inv.billing_address.postal_code]
                          .filter(Boolean).join(', ')}
                      </p>
                      {inv.billing_address.country && <p>{inv.billing_address.country}</p>}
                    </div>
                  )}
                </div>
              )}

              {/* Line items */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('Line Items')}</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="text-start px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Description')}</th>
                        <th className="text-end px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Qty')}</th>
                        <th className="text-end px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Rate')}</th>
                        <th className="text-end px-4 py-2.5 text-xs font-medium text-gray-500 dark:text-gray-400">{t('Amount')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {(inv.line_items || []).map((item: any, i: number) => (
                        <tr key={i}>
                          <td className="px-4 py-2.5 text-gray-900 dark:text-white">{item.description}</td>
                          <td className="px-4 py-2.5 text-end text-gray-600 dark:text-gray-400">{item.quantity}</td>
                          <td className="px-4 py-2.5 text-end text-gray-600 dark:text-gray-400">{formatCurrency(item.amount, inv.currency)}</td>
                          <td className="px-4 py-2.5 text-end font-medium text-gray-900 dark:text-white">
                            {formatCurrency(item.amount * item.quantity, inv.currency)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-72 space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{t('Subtotal')}</span>
                    <span className="font-normal text-gray-900 dark:text-white">{formatCurrency(inv.subtotal, inv.currency)}</span>
                  </div>
                  {inv.tax_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('Tax')} {inv.tax_rate ? `(${(inv.tax_rate * 100).toFixed(1)}%)` : ''}
                      </span>
                      <span className="font-normal text-gray-900 dark:text-white">{formatCurrency(inv.tax_amount, inv.currency)}</span>
                    </div>
                  )}
                  {inv.discount_amount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('Discount')} {inv.discount_rate ? `(${(inv.discount_rate * 100).toFixed(1)}%)` : ''}
                      </span>
                      <span className="font-normal text-success-600 dark:text-success-400">-{formatCurrency(inv.discount_amount, inv.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-gray-900 dark:text-white">{t('Total')}</span>
                    <span className="text-gray-900 dark:text-white">{formatCurrency(inv.total_amount, inv.currency)}</span>
                  </div>
                </div>
              </div>

              {/* Payment terms & recurring info */}
              {(inv.payment_terms || inv.is_recurring || inv.payment_method) && (
                <div className="flex flex-wrap gap-2">
                  {inv.payment_terms && (
                    <Badge color="blue">
                      {inv.payment_terms}
                    </Badge>
                  )}
                  {inv.is_recurring && inv.recurring_interval && (
                    <Badge color="purple">
                      Recurring · {inv.recurring_interval}
                    </Badge>
                  )}
                  {inv.payment_method && (
                    <Badge color="gray">
                      {inv.payment_method}
                    </Badge>
                  )}
                </div>
              )}

              {/* Notes */}
              {inv.notes && (
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('Notes')}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{inv.notes}</p>
                </div>
              )}

              {inv.internal_memo && (
                <div className="p-3 bg-warning-50 dark:bg-warning-900/10 rounded-lg border border-warning-200 dark:border-warning-800">
                  <p className="text-xs font-medium text-warning-600 dark:text-warning-400 mb-1">{t('Internal Memo')}</p>
                  <p className="text-sm text-warning-800 dark:text-warning-300 whitespace-pre-wrap">{inv.internal_memo}</p>
                </div>
              )}

              {/* Timeline */}
              {timelineEvents.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('Timeline')}</h3>
                  <div className="space-y-2">
                    {timelineEvents.map((evt, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400">
                          {evt.icon}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm text-gray-900 dark:text-white">{evt.label}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(evt.date).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment link */}
              {inv.stripe_payment_link && (
                <div className="p-3 bg-info-50 dark:bg-info-900/10 rounded-lg border border-info-200 dark:border-info-800">
                  <p className="text-xs font-medium text-info-600 dark:text-info-400 mb-1">{t('Payment Link')}</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={inv.stripe_payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-info-700 dark:text-info-300 hover:underline truncate flex-1"
                    >
                      {inv.stripe_payment_link}
                    </a>
                    <Button variant="outline" size="small" onClick={copyPaymentLink}>
                      <PiCopyLight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-20 text-gray-500">
              {t('Invoice not found')}
            </div>
          )}
        </div>

        {/* Footer - Actions */}
        {inv && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex gap-2">
              <Button variant="outline" size="small" onClick={handleDuplicate}>
                <PiCopySimpleLight className="w-3.5 h-3.5 me-1" />
                {t('Duplicate')}
              </Button>
              {['draft', 'void'].includes(inv.status) && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-md transition-colors"
                >
                  <PiTrashLight className="w-3.5 h-3.5" />
                  {t('Delete')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {inv.status === 'draft' && onEdit && (
                <Button variant="outline" size="small" onClick={() => { onEdit(inv as FinanceInvoice); onClose(); }}>
                  <PiPencilSimpleLight className="w-3.5 h-3.5 me-1" />
                  {t('Edit')}
                </Button>
              )}
              {['draft', 'sent', 'overdue'].includes(inv.status) && inv.status !== 'void' && (
                <Button variant="outline" size="small" onClick={handleVoid}>
                  <PiProhibitLight className="w-3.5 h-3.5 me-1" />
                  {t('Void')}
                </Button>
              )}
              {inv.status === 'draft' && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSend}
                  isLoading={loading?.sendInvoice}
                >
                  <PiPaperPlaneTiltLight className="w-3.5 h-3.5 me-1" />
                  {t('Send Invoice')}
                </Button>
              )}
              {inv.stripe_payment_link && (
                <Button variant="outline" size="small" onClick={copyPaymentLink}>
                  <PiCopyLight className="w-3.5 h-3.5 me-1" />
                  {t('Copy Payment Link')}
                </Button>
              )}
              {inv.status === 'sent' && (
                <Button
                  variant="outline"
                  size="small"
                  onClick={handleCheckPayment}
                  isLoading={checkingPayment}
                >
                  <PiArrowsClockwiseLight className="w-3.5 h-3.5 me-1" />
                  {t('Check Payment')}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Overlay */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px] rounded-lg" onClick={() => setShowDeleteConfirm(false)} />
            <div className="relative glass rounded-lg shadow-xl p-6 max-w-sm mx-auto text-center space-y-4">
              <div className="mx-auto w-10 h-10 rounded-full bg-danger-100 dark:bg-danger-900/30 flex items-center justify-center">
                <PiWarningLight className="w-5 h-5 text-danger-600 dark:text-danger-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{t('Delete Invoice')}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('This will permanently delete this invoice. This action cannot be undone.')}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" size="small" onClick={() => setShowDeleteConfirm(false)}>
                  {t('Cancel')}
                </Button>
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-danger-600 hover:bg-danger-700 rounded-md transition-colors"
                >
                  {t('Delete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Helper Components ────────────────────────────────────────

const InfoCard: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => (
  <div className="glass rounded-lg p-3">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-sm font-medium mt-0.5 ${highlight ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
      {value}
    </p>
  </div>
);

export default InvoiceDetailModal;
